import React, { useMemo } from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import {
  ReportField,
  ReportFlowPages,
  ReportFooter,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportNote,
  ReportSignLine,
  ReportSigns,
  ReportTitleRow,
} from '../../../components/ui/Report';
import type { CrewStatsNrRow } from '../types';

function dash(v: React.ReactNode) {
  if (v == null || v === '') return '—';
  return v;
}

function fmtDate(v?: string | null) {
  return v ? formatDateGlobal(v) : '—';
}

function fmtAvg(v?: number | null) {
  if (v == null || !Number.isFinite(v)) return '—';
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function sortNrRows(rows: CrewStatsNrRow[]): CrewStatsNrRow[] {
  return [...rows].sort((a, b) => {
    const ao = a.rankSortOrder ?? 9999;
    const bo = b.rankSortOrder ?? 9999;
    if (ao !== bo) return ao - bo;
    return a.name.localeCompare(b.name);
  });
}

function TableHead() {
  return (
    <table className="crew-list-print-table so-flow-table so-flow-table-head snr-flow-table">
      <thead>
        <tr>
          <th className="crew-list-print-col-no">No.</th>
          <th className="so-flow-col-name">Name</th>
          <th className="so-flow-col-rank">Rank</th>
          <th className="so-flow-col-nat">Nationality</th>
          <th className="crew-list-print-col-num">Age</th>
          <th className="so-flow-col-date">Appraisal</th>
          <th className="snr-flow-col-avg">Avg</th>
          <th className="snr-flow-col-eval">Evaluation</th>
          <th className="snr-flow-col-promote">Promote</th>
          <th className="so-flow-col-vessel">Last vessel</th>
          <th className="so-flow-col-date">Last S/Off</th>
        </tr>
      </thead>
    </table>
  );
}

function LineRow({ row, index }: { row: CrewStatsNrRow; index: number }) {
  return (
    <table className="crew-list-print-table so-flow-table so-flow-row-table snr-flow-table">
      <tbody>
        <tr>
          <td className="crew-list-print-col-no">{index + 1}</td>
          <td className="so-flow-col-name">{row.name}</td>
          <td className="so-flow-col-rank">{dash(row.rank)}</td>
          <td className="so-flow-col-nat">{dash(row.nationality)}</td>
          <td className="crew-list-print-col-num">{row.age ?? '—'}</td>
          <td className="so-flow-col-date">{fmtDate(row.lastAppraisalDate)}</td>
          <td className="snr-flow-col-avg">{fmtAvg(row.appraisalAvg)}</td>
          <td className="snr-flow-col-eval">{dash(row.overallEvaluation)}</td>
          <td className="snr-flow-col-promote">{row.toBePromoted ? 'Yes' : '—'}</td>
          <td className="so-flow-col-vessel">{dash(row.lastVessel)}</td>
          <td className="so-flow-col-date">{fmtDate(row.lastSignOffDate)}</td>
        </tr>
      </tbody>
    </table>
  );
}

export type StatsNotRecommendedReportProps = {
  rows: CrewStatsNrRow[];
  vesselLabel: string;
};

export const StatsNotRecommendedReport: React.FC<StatsNotRecommendedReportProps> = ({
  rows,
  vesselLabel,
}) => {
  const sortedRows = useMemo(() => sortNrRows(rows), [rows]);

  const firstHeader = useMemo(
    () => (
      <div className="so-flow-header">
        <ReportHeader>
          <ReportTitleRow title="Not recommended" formId="Operations" />
          <ReportMeta>
            <ReportMetaRow>
              <ReportField label="Scope" value={vesselLabel} />
              <ReportField label="Printed" value={fmtDate(new Date().toISOString().slice(0, 10))} />
              <ReportField label="Sorted by" value="Rank order" />
            </ReportMetaRow>
          </ReportMeta>
        </ReportHeader>
        <TableHead />
      </div>
    ),
    [vesselLabel],
  );

  const continueHeader = useMemo(
    () => (
      <div className="so-flow-header">
        <div className="so-flow-continue-bar">
          <span className="so-flow-continue-title">Not recommended</span>
          <span className="so-flow-continue-meta">{vesselLabel}</span>
          <span className="so-flow-continue-hint">continued</span>
        </div>
        <TableHead />
      </div>
    ),
    [vesselLabel],
  );

  const lastFooter = useMemo(
    () => (
      <ReportFooter>
        <ReportSigns>
          <ReportSignLine label="Prepared by" />
          <ReportSignLine label="Date" />
        </ReportSigns>
        <ReportNote>
          Crew flagged as not recommended, with last appraisal and tour context, ordered by rank.
        </ReportNote>
      </ReportFooter>
    ),
    [],
  );

  const { items, itemKeys } = useMemo(() => {
    if (sortedRows.length === 0) {
      return {
        items: [
          <table key="empty" className="crew-list-print-table so-flow-table">
            <tbody>
              <tr>
                <td colSpan={11} className="crew-list-print-empty">
                  No not-recommended crew for these filters.
                </td>
              </tr>
            </tbody>
          </table>,
        ],
        itemKeys: ['empty'],
      };
    }
    return {
      items: sortedRows.map((row, i) => <LineRow key={row.crewId} row={row} index={i} />),
      itemKeys: sortedRows.map((row) => String(row.crewId)),
    };
  }, [sortedRows]);

  return (
    <ReportFlowPages
      className="crew-list-print-report crew-list-print-stats-nr"
      size="landscape"
      firstHeader={firstHeader}
      continueHeader={continueHeader}
      lastFooter={lastFooter}
      items={items}
      itemKeys={itemKeys}
    />
  );
};

export default StatsNotRecommendedReport;
